import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import FormModal from '@shared/ui/FormModal';
import api from '../lib/api';

interface CalendarTriggerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTrigger: (options: { targetDate: string; forceRefresh: boolean }) => void;
  sourceName: string;
}

interface ChamberStatus {
  scraped: boolean;
  scrapedToday: boolean;
  eventCount: number;
  billCount: number;
  lastScrapedAt: string | null;
}

interface ScraperStatus {
  targetDate: string;
  targetDateFormatted: string;
  chambers: {
    House: ChamberStatus;
    Senate: ChamberStatus;
    Joint: ChamberStatus;
  };
  allChambersReady: boolean;
  totalEvents: number;
  warnings: string[];
}

const CalendarTriggerModal: React.FC<CalendarTriggerModalProps> = ({
  isOpen,
  onClose,
  onTrigger,
  sourceName
}) => {
  const [targetDate, setTargetDate] = useState<'tomorrow' | 'today' | 'custom'>('tomorrow');
  const [customDate, setCustomDate] = useState('');
  const [forceRefresh, setForceRefresh] = useState(false);
  const [status, setStatus] = useState<ScraperStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [showHtmlUpload, setShowHtmlUpload] = useState(false);
  const [houseFile, setHouseFile] = useState<File | null>(null);
  const [senateFile, setSenateFile] = useState<File | null>(null);
  const houseInputRef = useRef<HTMLInputElement>(null);
  const senateInputRef = useRef<HTMLInputElement>(null);

  const getMtParts = (date: Date) => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Denver',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(date);
    const partMap = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return {
      year: partMap.year,
      month: partMap.month,
      day: partMap.day
    };
  };
  
  const toMtDate = (date: Date) => {
    const { year, month, day } = getMtParts(date);
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0));
  };
  
  const formatDateDisplay = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      timeZone: 'America/Denver',
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateForApi = (date: Date) => {
    const { year, month, day } = getMtParts(date);
    return `${year}-${month}-${day}`;
  };

  /**
   * Get the next weekday (Monday-Friday) from the given date.
   * Legislative sessions rarely occur on weekends.
   * - If tomorrow is a weekday, returns tomorrow
   * - If tomorrow is Saturday, returns Monday
   * - If tomorrow is Sunday, returns Monday
   */
  const getNextWeekday = (baseDate: Date): Date => {
    const next = new Date(baseDate);
    next.setUTCDate(next.getUTCDate() + 1);
    
    const dayOfWeek = next.getUTCDay();
    
    // Saturday (6) -> add 2 days to get Monday
    if (dayOfWeek === 6) {
      next.setUTCDate(next.getUTCDate() + 2);
    }
    // Sunday (0) -> add 1 day to get Monday
    else if (dayOfWeek === 0) {
      next.setUTCDate(next.getUTCDate() + 1);
    }
    
    return next;
  };

  const today = toMtDate(new Date());
  const nextSessionDay = getNextWeekday(today);
  
  const todayStr = formatDateForApi(today);
  const nextSessionDayStr = formatDateForApi(nextSessionDay);

  // Fetch status when modal opens or target date changes
  useEffect(() => {
    if (!isOpen) return;

    const fetchStatus = async () => {
      setStatusLoading(true);
      try {
        let dateParam = nextSessionDayStr;
        if (targetDate === 'today') {
          dateParam = todayStr;
        } else if (targetDate === 'custom' && customDate) {
          dateParam = customDate;
        }
        
        const response = await api.get(`/data-management/calendar-scraper/status?date=${dateParam}`);
        setStatus(response.data.data);
      } catch (error) {
        console.error('Failed to fetch scraper status:', error);
        setStatus(null);
      } finally {
        setStatusLoading(false);
      }
    };

    fetchStatus();
  }, [isOpen, targetDate, customDate, todayStr, nextSessionDayStr]);

  const hasHtmlFiles = !!(houseFile || senateFile);

  const getDateValue = () => {
    if (targetDate === 'today') return todayStr;
    if (targetDate === 'custom' && customDate) return customDate;
    return nextSessionDayStr;
  };

  const handleTrigger = async () => {
    setLoading(true);
    const dateValue = getDateValue();

    if (hasHtmlFiles) {
      try {
        const formData = new FormData();
        if (houseFile) formData.append('house', houseFile);
        if (senateFile) formData.append('senate', senateFile);
        formData.append('targetDate', dateValue);
        formData.append('forceRefresh', String(forceRefresh));

        await api.post('/data-management/calendar-import/upload-html', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        onClose();
      } catch (err) {
        console.error('HTML upload import failed:', err);
      } finally {
        setLoading(false);
      }
      return;
    }

    onTrigger({ targetDate: dateValue, forceRefresh });
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      title={`Trigger ${sourceName}`}
      onClose={onClose}
      onSubmit={handleTrigger}
      size="md"
      submitLabel={hasHtmlFiles ? 'Import from HTML' : 'Run scraper'}
      isSubmitting={loading}
      submitDisabled={loading || (targetDate === 'custom' && !customDate)}
    >
      <div className="space-y-4">
          {/* Target Date Selection */}
          <div>
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
              Target Date
            </label>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-3">
              Which day&apos;s calendar events should be scraped?
            </p>
            
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-border-light dark:border-border-dark rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800">
                <input
                  type="radio"
                  name="targetDate"
                  checked={targetDate === 'tomorrow'}
                  onChange={() => setTargetDate('tomorrow')}
                  className="w-4 h-4 text-primary-500"
                />
                <div>
                  <div className="font-medium text-text-primary dark:text-text-dark-primary">
                    Next Session Day
                  </div>
                  <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    {formatDateDisplay(nextSessionDay)}
                  </div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border border-border-light dark:border-border-dark rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800">
                <input
                  type="radio"
                  name="targetDate"
                  checked={targetDate === 'today'}
                  onChange={() => setTargetDate('today')}
                  className="w-4 h-4 text-primary-500"
                />
                <div>
                  <div className="font-medium text-text-primary dark:text-text-dark-primary">
                    Today
                  </div>
                  <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    {formatDateDisplay(today)} — Use for morning catch-up if last night&apos;s scrape missed data
                  </div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border border-border-light dark:border-border-dark rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800">
                <input
                  type="radio"
                  name="targetDate"
                  checked={targetDate === 'custom'}
                  onChange={() => setTargetDate('custom')}
                  className="w-4 h-4 text-primary-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-text-primary dark:text-text-dark-primary">
                    Custom Date
                  </div>
                  {targetDate === 'custom' && (
                    <input
                      type="date"
                      value={customDate}
                      onChange={(e) => setCustomDate(e.target.value)}
                      className="mt-2 w-full p-2 border border-border-light dark:border-border-dark rounded bg-white dark:bg-background-dark-primary text-text-primary dark:text-text-dark-primary"
                    />
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Current Status */}
          {statusLoading ? (
            <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
              <div className="animate-pulse flex items-center gap-2">
                <div className="w-4 h-4 bg-neutral-300 dark:bg-neutral-600 rounded-full"></div>
                <div className="h-4 bg-neutral-300 dark:bg-neutral-600 rounded w-48"></div>
              </div>
            </div>
          ) : status && (
            <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg space-y-3">
              <h4 className="font-medium text-text-primary dark:text-text-dark-primary">
                Current Status for {status.targetDateFormatted}
              </h4>
              
              <div className="grid grid-cols-3 gap-2 text-sm">
                {(['House', 'Senate', 'Joint'] as const).map((chamber) => {
                  const chamberStatus = status.chambers[chamber];
                  return (
                    <div key={chamber} className="text-center p-2 bg-white dark:bg-background-dark-secondary rounded">
                      <div className="font-medium">{chamber}</div>
                      {chamberStatus.scrapedToday ? (
                        <div className="flex items-center justify-center gap-1 text-green-600">
                          <CheckCircle className="w-3 h-3" />
                          <span>{chamberStatus.eventCount} events</span>
                        </div>
                      ) : chamberStatus.scraped ? (
                        <div className="text-yellow-600 text-xs">
                          Scraped (not today)
                        </div>
                      ) : (
                        <div className="text-text-tertiary dark:text-text-dark-tertiary text-xs">
                          Not scraped
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {status.allChambersReady && (
                <div className="flex items-center gap-2 text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Both House and Senate have been scraped today
                </div>
              )}

              {status.warnings && status.warnings.length > 0 && (
                <div className="space-y-1">
                  {status.warnings.map((warning, i) => (
                    <div key={i} className="flex items-center gap-2 text-amber-600 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      {warning}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Force Refresh Option */}
          <label className="flex items-start gap-3 p-3 border border-border-light dark:border-border-dark rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800">
            <input
              type="checkbox"
              checked={forceRefresh}
              onChange={(e) => setForceRefresh(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-primary-500"
            />
            <div>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                <span className="font-medium text-text-primary dark:text-text-dark-primary">
                  Force Refresh
                </span>
              </div>
              <div className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                Re-scrape even if both chambers have already been scraped today. Use this if you know the legislative website has updated.
              </div>
            </div>
          </label>

          {/* HTML Upload Fallback */}
          <div className="border border-border-light dark:border-border-dark rounded-lg">
            <button
              type="button"
              onClick={() => setShowHtmlUpload(!showHtmlUpload)}
              className="flex items-center justify-between w-full p-3 text-left hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary" />
                <span className="font-medium text-text-primary dark:text-text-dark-primary text-sm">
                  Import from HTML files
                </span>
              </div>
              {showHtmlUpload
                ? <ChevronUp className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                : <ChevronDown className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />}
            </button>

            {showHtmlUpload && (
              <div className="px-3 pb-3 space-y-3">
                <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
                  If the scraper is being rate-limited, save these pages from your browser and upload them here:
                </p>
                <div className="flex flex-col gap-1 text-xs">
                  <a
                    href="https://s3.us-west-2.amazonaws.com/accessible-docs.coleg.gov/house_calendar.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:underline truncate"
                  >
                    House Calendar (S3)
                  </a>
                  <a
                    href="https://s3.us-west-2.amazonaws.com/accessible-docs.coleg.gov/senate_calendar.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:underline truncate"
                  >
                    Senate Calendar (S3)
                  </a>
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                      House Calendar HTML
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        ref={houseInputRef}
                        type="file"
                        accept=".html,.htm"
                        onChange={(e) => setHouseFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => houseInputRef.current?.click()}
                        className="text-xs px-3 py-1.5 border border-border-light dark:border-border-dark rounded hover:bg-neutral-50 dark:hover:bg-neutral-800 text-text-primary dark:text-text-dark-primary"
                      >
                        {houseFile ? houseFile.name : 'Choose file...'}
                      </button>
                      {houseFile && (
                        <button
                          type="button"
                          onClick={() => { setHouseFile(null); if (houseInputRef.current) houseInputRef.current.value = ''; }}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                      Senate Calendar HTML
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        ref={senateInputRef}
                        type="file"
                        accept=".html,.htm"
                        onChange={(e) => setSenateFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => senateInputRef.current?.click()}
                        className="text-xs px-3 py-1.5 border border-border-light dark:border-border-dark rounded hover:bg-neutral-50 dark:hover:bg-neutral-800 text-text-primary dark:text-text-dark-primary"
                      >
                        {senateFile ? senateFile.name : 'Choose file...'}
                      </button>
                      {senateFile && (
                        <button
                          type="button"
                          onClick={() => { setSenateFile(null); if (senateInputRef.current) senateInputRef.current.value = ''; }}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

    </FormModal>
  );
};

export default CalendarTriggerModal;
